import { useState, useEffect, useCallback } from "react";
import type { ComparisonReport, SessionReport, SectorTime, TrendEntry } from "../types/analysis";
import { apiFetch } from "../apiFetch";
import { canonicalizeChannelName } from "../utils/channelAliases";

function normalizeReportChannels(report: SessionReport): SessionReport {
  const mergedSamples: Record<string, { timestamps: number[]; values: number[] }> = {};

  for (const [rawChannel, series] of Object.entries(report.samples)) {
    const channel = canonicalizeChannelName(rawChannel);
    const existing = mergedSamples[channel];
    if (!existing) {
      mergedSamples[channel] = {
        timestamps: [...series.timestamps],
        values: [...series.values],
      };
      continue;
    }

    existing.timestamps.push(...series.timestamps);
    existing.values.push(...series.values);
  }

  const channels = [...new Set(report.channels.map((channel) => canonicalizeChannelName(channel)))].sort();

  const channelStatistics = report.channelStatistics
    ? Object.fromEntries(
      Object.entries(report.channelStatistics).map(([channel, stats]) => [canonicalizeChannelName(channel), stats]),
    )
    : undefined;

  const lapChannelStatistics = report.lapChannelStatistics
    ? Object.fromEntries(
      Object.entries(report.lapChannelStatistics).map(([lapKey, statsByChannel]) => [
        lapKey,
        Object.fromEntries(
          Object.entries(statsByChannel).map(([channel, stats]) => [canonicalizeChannelName(channel), stats]),
        ),
      ]),
    )
    : undefined;

  return {
    ...report,
    channels,
    samples: mergedSamples,
    channelStatistics,
    lapChannelStatistics,
  };
}

function normalizeComparisonReport(report: ComparisonReport): ComparisonReport {
  return {
    ...report,
    reportA: normalizeReportChannels(report.reportA),
    reportB: normalizeReportChannels(report.reportB),
  };
}


/** Fetches and caches the full report for a session. */
export function useReport(sessionId: number | null) {
  const [report, setReport] = useState<SessionReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    if (sessionId === null) {
      setReport(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/sessions/${sessionId}/report`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: SessionReport = await res.json();
      setReport(normalizeReportChannels(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return { report, loading, error, refresh: fetchReport };
}

/** Fetches and caches a comparison report for two sessions. */
export function useComparisonReport(sessionAId: number | null, sessionBId: number | null) {
  const [comparison, setComparison] = useState<ComparisonReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchComparison = useCallback(async () => {
    if (sessionAId === null || sessionBId === null) {
      setComparison(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        idA: String(sessionAId),
        idB: String(sessionBId),
      });
      const res = await apiFetch(`/api/sessions/compare/report?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: ComparisonReport = await res.json();
      setComparison(normalizeComparisonReport(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [sessionAId, sessionBId]);

  useEffect(() => {
    fetchComparison();
  }, [fetchComparison]);

  return { comparison, loading, error, refresh: fetchComparison };
}

/** Manages sector computation and retrieval for a session. */
export function useSectors(sessionId: number | null) {
  const [sectors, setSectors] = useState<Record<string, SectorTime[]>>({});
  const [source, setSource] = useState<"live" | "auto" | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSectors = useCallback(async () => {
    if (sessionId === null) return;

    try {
      const res = await apiFetch(`/api/sessions/${sessionId}/sectors`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSectors(data.sectors ?? {});
      setSource(data.source ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [sessionId]);

  const computeSectors = useCallback(
    async (sectorCount?: number) => {
      if (sessionId === null) return;

      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch(`/api/sessions/${sessionId}/sectors`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sectorCount }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setSectors(data.sectors ?? {});
        setSource(data.source ?? null);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [sessionId],
  );

  useEffect(() => {
    fetchSectors();
  }, [fetchSectors]);

  return { sectors, source, loading, error, computeSectors, refresh: fetchSectors };
}

/** Fetches cross-session trend data. */
export function useTrends(driverId?: number | null, limit?: number) {
  const [trends, setTrends] = useState<TrendEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTrends = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (driverId) params.set("driverId", String(driverId));
      if (limit) params.set("limit", String(limit));
      const res = await apiFetch(`/api/sessions/trends?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTrends(data.trends ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [driverId, limit]);

  useEffect(() => {
    fetchTrends();
  }, [fetchTrends]);

  return { trends, loading, error, refresh: fetchTrends };
}
